import { type ReactElement, useCallback, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  type ChartSeriesPlan,
  coerceChartNumber,
  MULTI_SERIES_LINE_MAX_SERIES,
  pivotMultiSeriesLineRows,
  sortRowsForChart,
} from "@blis/insights/explore/chart-derive";

import { DonutChart } from "@/components/charts/donut-chart";
import { cn } from "@/components/ui/utils";
import {
  type AvailableChartColorsKeys,
  constructCategoryColors,
  getChartColorHex,
  getColorClassName,
} from "@/utils/chart-utils";
import { formatExploreDateOnly } from "../explore/formatValue";

type ExploreReportChartProps = {
  rows: readonly Record<string, unknown>[];
  plan: ChartSeriesPlan;
  labels: ReadonlyMap<string, string>;
};

type ChartRow = Record<string, unknown> & {
  __categoryLabel: string;
};

const CHART_LIMIT = 40;
const DONUT_MAX_GROUPS = 8;
const CHART_CATEGORY_COLORS = [
  "blue",
  "emerald",
  "violet",
  "amber",
  "cyan",
  "pink",
  "lime",
  "fuchsia",
] as const satisfies readonly AvailableChartColorsKeys[];
const LINE_SERIES_COLORS = CHART_CATEGORY_COLORS;
const PRIMARY_CHART_COLOR = "blue" as const;
const DONUT_CHART_COLORS = CHART_CATEGORY_COLORS;

const chartTooltipCursor = {
  fill: "var(--muted)",
  fillOpacity: 0.35,
};

/** Recharts wraps custom content in a default white box unless cleared. */
const chartTooltipWrapperStyle = {
  outline: "none",
  backgroundColor: "transparent",
  border: "none",
  boxShadow: "none",
  padding: 0,
};

export function ExploreReportChart({
  rows,
  plan,
  labels,
}: ExploreReportChartProps) {
  const multiSeries = useMemo(() => {
    if (plan.kind !== "line" || plan.seriesKey == null) {
      return null;
    }
    return pivotMultiSeriesLineRows(
      rows,
      { ...plan, seriesKey: plan.seriesKey },
      labels,
      {
        maxPoints: CHART_LIMIT,
        maxSeries: MULTI_SERIES_LINE_MAX_SERIES,
        formatXLabel: (value, field) =>
          formatCategoryLabel(value, field, labels),
        formatSeriesName: (value, field) =>
          formatCategoryLabel(value, field, labels),
      },
    );
  }, [labels, plan, rows]);

  const chartRows = useMemo(
    () => (multiSeries ? multiSeries.rows : buildChartRows(rows, plan, labels)),
    [labels, multiSeries, plan, rows],
  );
  const isTruncated =
    multiSeries != null
      ? multiSeries.pointsTruncated || multiSeries.seriesTruncated
      : rows.length > chartRows.length;
  const yLabel = labels.get(plan.yKey) ?? plan.yKey;

  const seriesColors = useMemo(() => {
    if (!multiSeries) {
      return new Map<string, AvailableChartColorsKeys>();
    }
    return constructCategoryColors(multiSeries.seriesNames, [
      ...LINE_SERIES_COLORS,
    ]);
  }, [multiSeries]);

  const truncationMessage = useMemo(() => {
    if (!isTruncated) {
      return null;
    }
    if (multiSeries) {
      const parts: string[] = [];
      if (multiSeries.seriesTruncated) {
        parts.push(
          `the top ${multiSeries.seriesNames.length.toLocaleString()} of ${multiSeries.totalSeriesCount.toLocaleString()} series (by total ${yLabel.toLowerCase()})`,
        );
      }
      if (multiSeries.pointsTruncated) {
        parts.push(
          `the first ${chartRows.length.toLocaleString()} time periods`,
        );
      }
      if (parts.length === 0) {
        return null;
      }
      return `Chart shows ${parts.join(" and ")}. Use the table below for the full result.`;
    }
    return `Chart shows the first ${chartRows.length.toLocaleString()} groups. Use the table below for the full result.`;
  }, [chartRows.length, isTruncated, multiSeries, yLabel]);

  const useHorizontalBar =
    plan.kind === "bar" && plan.direction === "horizontal";

  const primaryFillClass = getColorClassName(PRIMARY_CHART_COLOR, "fill");
  const primaryStrokeColor = getChartColorHex(PRIMARY_CHART_COLOR);

  if (plan.kind === "line" && multiSeries != null) {
    return (
      <div className="flex h-[clamp(22rem,52vh,38rem)] w-full shrink-0 flex-col border-b bg-background px-4 py-3">
        <ExploreMultiSeriesLineChart
          chartRows={chartRows}
          seriesNames={multiSeries.seriesNames}
          seriesColors={seriesColors}
          yLabel={yLabel}
        />
        {truncationMessage ? (
          <p className="shrink-0 pt-1 text-xs text-muted-foreground">
            {truncationMessage}
          </p>
        ) : null}
      </div>
    );
  }

  const chart = renderExploreChart({
    plan,
    chartRows,
    yLabel,
    useHorizontalBar,
    primaryFillClass,
    primaryStrokeColor,
  });

  if (plan.kind === "donut") {
    return (
      <div className="w-full shrink-0 border-b bg-background px-4 py-4">
        <ExploreDonutView chartRows={chartRows} plan={plan} yLabel={yLabel} />
        {isTruncated ? (
          <p className="shrink-0 pt-2 text-xs text-muted-foreground">
            Chart shows the first {chartRows.length.toLocaleString()} groups.
            Use the table below for the full result.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex h-[clamp(22rem,52vh,38rem)] w-full shrink-0 flex-col border-b bg-background px-4 py-3">
      <div className="relative min-h-[240px] w-full flex-1">
        {chart ? (
          <ResponsiveContainer width="100%" height="100%">
            {chart}
          </ResponsiveContainer>
        ) : (
          <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Chart type not supported for this grouping.
          </p>
        )}
      </div>
      {truncationMessage ? (
        <p className="shrink-0 pt-1 text-xs text-muted-foreground">
          {truncationMessage}
        </p>
      ) : null}
    </div>
  );
}

function ExploreMultiSeriesLineChart({
  chartRows,
  seriesNames,
  seriesColors,
  yLabel,
}: {
  chartRows: ChartRow[];
  seriesNames: string[];
  seriesColors: Map<string, AvailableChartColorsKeys>;
  yLabel: string;
}) {
  const [hiddenSeries, setHiddenSeries] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const toggleSeries = useCallback((seriesName: string) => {
    setHiddenSeries((current) => {
      const next = new Set(current);
      if (next.has(seriesName)) {
        next.delete(seriesName);
      } else {
        next.add(seriesName);
      }
      return next;
    });
  }, []);

  const activeHiddenSeries = useMemo(() => {
    const names = new Set(seriesNames);
    return new Set([...hiddenSeries].filter((name) => names.has(name)));
  }, [hiddenSeries, seriesNames]);

  const visibleSeriesCount = seriesNames.length - activeHiddenSeries.size;

  return (
    <>
      <ExploreMultiSeriesLegend
        seriesNames={seriesNames}
        seriesColors={seriesColors}
        hiddenSeries={activeHiddenSeries}
        onToggleSeries={toggleSeries}
      />
      <div className="relative min-h-[240px] w-full flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartRows}
            margin={{ top: 4, right: 16, left: 8, bottom: 8 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              className="stroke-border"
              vertical={false}
            />
            <XAxis
              dataKey="__categoryLabel"
              interval={0}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              angle={chartRows.length > 10 ? -35 : 0}
              textAnchor={chartRows.length > 10 ? "end" : "middle"}
              height={chartRows.length > 10 ? 72 : 32}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11 }}
              className="fill-muted-foreground"
              width={56}
            />
            <Tooltip
              cursor={chartTooltipCursor}
              wrapperStyle={chartTooltipWrapperStyle}
              content={(props) => (
                <ExploreChartTooltip
                  {...props}
                  yLabel={yLabel}
                  multiSeries
                  seriesColors={seriesColors}
                  hiddenSeries={activeHiddenSeries}
                />
              )}
            />
            {seriesNames.map((seriesName) => {
              const color = seriesColors.get(seriesName) ?? PRIMARY_CHART_COLOR;
              const strokeColor = getChartColorHex(color);
              const hidden = activeHiddenSeries.has(seriesName);
              return (
                <Line
                  key={seriesName}
                  type="monotone"
                  name={seriesName}
                  dataKey={seriesName}
                  strokeWidth={2}
                  dot={{ r: 2, fill: strokeColor, stroke: strokeColor }}
                  stroke={strokeColor}
                  hide={hidden}
                  connectNulls
                  isAnimationActive={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
        {visibleSeriesCount === 0 ? (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
            Select a series in the legend to display lines.
          </p>
        ) : null}
      </div>
    </>
  );
}

function ExploreMultiSeriesLegend({
  seriesNames,
  seriesColors,
  hiddenSeries,
  onToggleSeries,
}: {
  seriesNames: string[];
  seriesColors: Map<string, AvailableChartColorsKeys>;
  hiddenSeries: ReadonlySet<string>;
  onToggleSeries: (seriesName: string) => void;
}) {
  return (
    <ul
      className="mb-2 flex max-h-16 flex-wrap justify-end gap-x-1 gap-y-1 overflow-y-auto"
      aria-label="Chart series"
    >
      {seriesNames.map((seriesName) => {
        const color = seriesColors.get(seriesName) ?? PRIMARY_CHART_COLOR;
        const hidden = hiddenSeries.has(seriesName);
        return (
          <li key={seriesName}>
            <button
              type="button"
              aria-pressed={!hidden}
              title={hidden ? `Show ${seriesName}` : `Hide ${seriesName}`}
              className={cn(
                "inline-flex max-w-48 items-center gap-1.5 rounded px-2 py-0.5 text-xs transition",
                "cursor-pointer hover:bg-muted",
                hidden && "opacity-45",
              )}
              onClick={() => onToggleSeries(seriesName)}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "size-2 shrink-0 rounded-sm",
                  getColorClassName(color, "bg"),
                  hidden && "opacity-50",
                )}
              />
              <span className="truncate text-muted-foreground">
                {seriesName}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Must return a Recharts chart element — ResponsiveContainer clones its direct child. */
function renderExploreChart({
  plan,
  chartRows,
  yLabel,
  useHorizontalBar,
  primaryFillClass,
  primaryStrokeColor,
}: {
  plan: ChartSeriesPlan;
  chartRows: ChartRow[];
  yLabel: string;
  useHorizontalBar: boolean;
  primaryFillClass: string;
  primaryStrokeColor: string;
}): ReactElement | null {
  if (plan.kind === "line") {
    return (
      <LineChart
        data={chartRows}
        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          className="stroke-border"
          vertical={false}
        />
        <XAxis
          dataKey="__categoryLabel"
          interval={0}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11 }}
          className="fill-muted-foreground"
          angle={chartRows.length > 10 ? -35 : 0}
          textAnchor={chartRows.length > 10 ? "end" : "middle"}
          height={chartRows.length > 10 ? 72 : 32}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11 }}
          className="fill-muted-foreground"
          width={56}
        />
        <Tooltip
          cursor={chartTooltipCursor}
          wrapperStyle={chartTooltipWrapperStyle}
          content={(props) => (
            <ExploreChartTooltip {...props} yLabel={yLabel} />
          )}
        />
        <Line
          type="monotone"
          dataKey={plan.yKey}
          strokeWidth={2}
          dot={{ r: 3, fill: primaryStrokeColor, stroke: primaryStrokeColor }}
          stroke={primaryStrokeColor}
          isAnimationActive={false}
        />
      </LineChart>
    );
  }

  if (plan.kind === "bar" && useHorizontalBar) {
    return (
      <BarChart
        layout="vertical"
        data={chartRows}
        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          className="stroke-border"
          horizontal={false}
        />
        <XAxis
          type="number"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11 }}
          className="fill-muted-foreground"
        />
        <YAxis
          type="category"
          dataKey="__categoryLabel"
          width={160}
          interval={0}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11 }}
          className="fill-muted-foreground"
        />
        <Tooltip
          cursor={chartTooltipCursor}
          wrapperStyle={chartTooltipWrapperStyle}
          content={(props) => (
            <ExploreChartTooltip {...props} yLabel={yLabel} />
          )}
        />
        <Bar
          dataKey={plan.yKey}
          radius={[0, 4, 4, 0]}
          className={primaryFillClass}
          fill=""
          isAnimationActive={false}
        />
      </BarChart>
    );
  }

  if (plan.kind === "bar") {
    return (
      <BarChart
        data={chartRows}
        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          className="stroke-border"
          vertical={false}
        />
        <XAxis
          dataKey="__categoryLabel"
          interval={0}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11 }}
          className="fill-muted-foreground"
          angle={chartRows.length > 8 ? -35 : 0}
          textAnchor={chartRows.length > 8 ? "end" : "middle"}
          height={chartRows.length > 8 ? 72 : 32}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11 }}
          className="fill-muted-foreground"
          width={56}
        />
        <Tooltip
          cursor={chartTooltipCursor}
          wrapperStyle={chartTooltipWrapperStyle}
          content={(props) => (
            <ExploreChartTooltip {...props} yLabel={yLabel} />
          )}
        />
        <Bar
          dataKey={plan.yKey}
          radius={[4, 4, 0, 0]}
          className={primaryFillClass}
          fill=""
          isAnimationActive={false}
        />
      </BarChart>
    );
  }

  return null;
}

function ExploreDonutView({
  chartRows,
  plan,
  yLabel,
}: {
  chartRows: ChartRow[];
  plan: ChartSeriesPlan;
  yLabel: string;
}) {
  const donutData = useMemo(
    () =>
      chartRows.map((row) => ({
        category: row.__categoryLabel,
        value: coerceChartNumber(row[plan.yKey]) ?? 0,
      })),
    [chartRows, plan.yKey],
  );

  const categoryColors = useMemo(
    () =>
      constructCategoryColors(
        donutData.map((row) => row.category),
        [...DONUT_CHART_COLORS],
      ),
    [donutData],
  );

  const total = useMemo(
    () => donutData.reduce((sum, row) => sum + row.value, 0),
    [donutData],
  );

  if (donutData.length === 0) {
    return null;
  }

  if (donutData.length > DONUT_MAX_GROUPS) {
    return (
      <div className="flex min-h-[12rem] flex-col items-center justify-center gap-1 px-6 py-8 text-center text-sm text-muted-foreground">
        <p>
          Donut charts support up to {DONUT_MAX_GROUPS} groups (this summary has{" "}
          {donutData.length.toLocaleString()}).
        </p>
        <p>Switch to Bar chart or narrow your filters.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto grid w-full max-w-2xl grid-cols-1 items-center gap-6 sm:grid-cols-2 sm:gap-8">
      <DonutChart
        data={donutData}
        category="category"
        value="value"
        colors={[...DONUT_CHART_COLORS]}
        className="mx-auto h-44 w-full max-w-[220px]"
        showLabel
        valueFormatter={formatMeasureValue}
      />
      <div className="min-w-0">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          {yLabel}
        </p>
        <ul className="flex max-h-52 flex-col gap-1 overflow-y-auto">
          {donutData.map((row) => {
            const color =
              categoryColors.get(row.category) ?? DONUT_CHART_COLORS[0];
            const share =
              total > 0 ? Math.round((row.value / total) * 1000) / 10 : 0;
            return (
              <li key={row.category} className="flex gap-3 rounded-md p-1">
                <span
                  aria-hidden="true"
                  className={cn(
                    "w-1 shrink-0 rounded",
                    getColorClassName(color, "bg"),
                  )}
                />
                <div className="min-w-0 flex-1 leading-snug">
                  <p className="truncate font-medium tabular-nums">
                    {formatMeasureValue(row.value)}
                    <span className="ms-1.5 text-xs font-normal text-muted-foreground">
                      ({share}%)
                    </span>
                  </p>
                  <p
                    className="truncate text-sm text-muted-foreground"
                    title={row.category}
                  >
                    {row.category}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function ExploreChartTooltip({
  active,
  payload,
  label,
  yLabel,
  multiSeries = false,
  seriesColors,
  hiddenSeries,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ name?: unknown; value?: unknown }>;
  label?: string | number;
  yLabel: string;
  multiSeries?: boolean;
  seriesColors?: Map<string, AvailableChartColorsKeys>;
  hiddenSeries?: ReadonlySet<string>;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  if (multiSeries && seriesColors) {
    const entries = payload
      .filter(
        (entry) =>
          entry.value != null &&
          Number.isFinite(Number(entry.value)) &&
          entry.name != null,
      )
      .map((entry) => ({
        name: String(entry.name),
        value: Number(entry.value),
      }))
      .filter((entry) => hiddenSeries == null || !hiddenSeries.has(entry.name))
      .sort((left, right) => right.value - left.value);

    if (entries.length === 0) {
      return null;
    }

    return (
      <div className="max-w-[14rem] rounded-md border border-border bg-popover px-2.5 py-2 text-xs text-popover-foreground shadow-md">
        <p
          className="mb-1.5 truncate font-medium leading-none"
          title={label != null ? String(label) : undefined}
        >
          {label != null ? String(label) : "—"}
        </p>
        <ul className="max-h-48 space-y-1 overflow-y-auto">
          {entries.map((entry) => {
            const color = seriesColors.get(entry.name) ?? PRIMARY_CHART_COLOR;
            return (
              <li
                key={entry.name}
                className="flex items-center justify-between gap-2"
              >
                <span className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className={cn(
                      "size-2 shrink-0 rounded-sm",
                      getColorClassName(color, "bg"),
                    )}
                  />
                  <span
                    className="truncate text-muted-foreground"
                    title={entry.name}
                  >
                    {entry.name}
                  </span>
                </span>
                <span className="shrink-0 font-medium tabular-nums">
                  {formatMeasureValue(entry.value)}
                </span>
              </li>
            );
          })}
        </ul>
        <p
          className="mt-1.5 truncate text-[10px] text-muted-foreground"
          title={yLabel}
        >
          {yLabel}
        </p>
      </div>
    );
  }

  const raw = payload[0]?.value;
  const value =
    typeof raw === "number"
      ? raw
      : typeof raw === "string"
        ? Number(raw)
        : Number.NaN;

  return (
    <div className="max-w-[14rem] rounded-md border border-border bg-popover px-2.5 py-2 text-xs text-popover-foreground shadow-md">
      <p
        className="truncate font-medium"
        title={label != null ? String(label) : undefined}
      >
        {label != null ? String(label) : "—"}
      </p>
      <p className="mt-0.5 truncate text-muted-foreground" title={yLabel}>
        <span className="font-medium tabular-nums text-popover-foreground">
          {formatMeasureValue(value)}
        </span>
        <span className="mx-1">·</span>
        {yLabel}
      </p>
    </div>
  );
}

function buildChartRows(
  rows: readonly Record<string, unknown>[],
  plan: ChartSeriesPlan,
  labels: ReadonlyMap<string, string>,
): ChartRow[] {
  return sortRowsForChart(rows, plan)
    .slice(0, CHART_LIMIT)
    .map((row) => ({
      ...row,
      [plan.yKey]: coerceChartNumber(row[plan.yKey]),
      __categoryLabel: formatCategoryLabel(row[plan.xKey], plan.xKey, labels),
    }));
}

function formatCategoryLabel(
  value: unknown,
  field: string,
  labels: ReadonlyMap<string, string>,
): string {
  if (value == null || value === "") {
    return "—";
  }
  if (value instanceof Date) {
    return formatExploreDateOnly(value.toISOString());
  }
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return formatExploreDateOnly(value);
    }
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return String(value);
  }
  return labels.get(field) ?? String(value);
}

function formatMeasureValue(value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
